/**
 * JOProducts — Featured plants from /api/products
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sun, Droplets, Calendar, Snowflake, Ruler, Sparkles, Bug, Apple, Utensils, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useSettings } from "@/lib/useSettings";

const ICON_MAP = { Sun, Droplets, Calendar, Snowflake, Ruler, Sparkles, Bug, Apple, Utensils, AlertTriangle };

function formatPrice(cents) {
  if (!cents) return "";
  const v = cents / 100;
  return v.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(".", ",") + " €";
}

export default function JOProducts() {
  const [products, setProducts] = useState([]);
  const { settings: s } = useSettings();

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await api.get("/products", { params: { limit: 8 } });
        const list = Array.isArray(res.data) ? res.data : (res.data?.items || []);
        if (!cancel) setProducts(list.filter(p => p.is_published).slice(0, 4));
      } catch { /* ignore */ }
    })();
    return () => { cancel = true; };
  }, []);

  if (!s?.shop_enabled) return null;
  if (!products.length) return null;

  return (
    <section className="jo-section" data-testid="jo-products">
      <div className="jo-container">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <div className="jo-section-eyebrow">— Coups de cœur de la saison</div>
            <h2 className="jo-section-title">Belles plantes,<br />prix doux.</h2>
          </div>
          <Link to="/shop" className="jo-btn-secondary self-start" data-testid="jo-products-shop-link">
            Toute la boutique
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {products.map(p => (
            <Link
              key={p.id}
              to={`/shop/produit/${p.slug}`}
              className="jo-product-card"
              data-testid={`jo-product-${p.slug}`}
            >
              <div className="jo-product-image">
                {p.featured_image && <img src={p.featured_image} alt={p.name} loading="lazy" />}
              </div>
              <div className="jo-product-body">
                <div className="jo-product-name">{p.name}</div>
                {(p.indicators || []).length > 0 && (
                  <div className="jo-product-indicators">
                    {p.indicators.slice(0, 3).map((ind, i) => {
                      const Icon = ICON_MAP[ind.icon] || Sparkles;
                      return (
                        <span key={i} className="jo-product-indicator" title={`${ind.label} — ${ind.value}`}>
                          <Icon className="w-3 h-3" style={{ color: ind.color || "var(--jdo-green)" }} />
                          {ind.value}
                        </span>
                      );
                    })}
                  </div>
                )}
                <div className="jo-product-footer">
                  <span className="jo-product-price">{formatPrice(p.price_cents)}</span>
                  <span className="jo-product-cta">
                    <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
